import { Component, DestroyRef, OnInit } from '@angular/core';
import { API, TemplateGroupDTO, TemplateSourceDTO } from '../../../../services/api/api';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { UtilService } from '../../../../services/util.service';
import { NavigationService } from '@ngx-seatback/utils';
import { RoutingID } from '../../../../routes/sb-routing-enum';
import { QueryParamService } from '../../../../services/lists/query-params';
import { TemplateGroupsComponent } from '../../templateGroup/template-groups.component';
import { TranslateService } from '@ngx-translate/core';
import { PermissionService } from '../../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SbWizardComponent, SbWizardTabComponent } from '@ngx-seatback/layout';

@Component({
  selector: 'sb-page-template-source-edit',
  templateUrl: './template-source-edit.component.html',
  styleUrls: ['./template-source-edit.component.scss'],
  standalone: false,
})
export class TemplateSourceEditComponent implements OnInit {
  templateSource: TemplateSourceDTO;
  templateGroup: TemplateGroupDTO;
  templateGroupId: number;
  templateSourceId: number;
  loading: boolean;
  form: UntypedFormGroup;

  constructor(
    private api: API,
    public errorHandler: ErrorService,
    public modal: ModalService,
    public util: UtilService,
    public navService: NavigationService,
    public queryParamService: QueryParamService,
    private translate: TranslateService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef
  ) {
    this.templateGroup = undefined;

    this.templateSourceId = null;
    this.templateGroupId = null;
    this.loading = true;
    this.form = new UntypedFormGroup({
      Name: new UntypedFormControl('', [Validators.required]),
    });
  }

  get Name() {
    return this.form.get('Name');
  }

  ngOnInit(): void {
    this.templateSourceId = +this.queryParamService.getQueryParam('templateSourceID');
    this.templateGroupId = +this.queryParamService.getQueryParam('templateGroupID');
    if (this.templateGroupId && this.templateGroupId !== -1) {
      this.api
        .mm_templating_provider_TemplateGroup_retrieveById(this.templateGroupId)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (group: TemplateGroupDTO) => {
            this.templateGroup = group;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
    this.refreshFromServer();

    this.registerFormChangesListener();
  }

  private registerFormChangesListener() {
    this.Name.valueChanges.subscribe(value => {
      this.templateSource.name = value;
    });
  }

  updateForm() {
    this.Name.setValue(this.templateSource.name);
    this.Name.markAsTouched();
  }

  nextStep(wizard: SbWizardComponent, nextTab: SbWizardTabComponent) {
    wizard.selectTab(nextTab);
  }

  private refreshFromServer() {
    this.loading = true;
    if (!this.templateSourceId) {
      this.templateSource = new TemplateSourceDTO();
      this.loading = false;
    } else {
      this.api
        .mm_templating_provider_TemplateSource_retrieveById(this.templateSourceId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (templateSource: TemplateSourceDTO) => {
            this.templateSource = templateSource;
            this.updateForm();
            this.Name.disable();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  updateTemplateSource() {
    if (this.templateSource?.id) {
      return;
    }
    this.loading = true;
    if (this.templateSource.name && this.templateGroupId !== -1) {
      this.api
        .mm_templating_provider_TemplateGroup_retrieveById(this.templateGroupId)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (templateGroup: TemplateGroupDTO) => {
            this.templateSource.templateGroup = templateGroup;
            this.api
              .mm_templating_provider_TemplateSource_create(this.templateSource)
              .pipe(
                takeUntilDestroyed(this.destroyRef),
                take(1),
                finalize(() => {
                  this.loading = false;
                })
              )
              .subscribe({
                next: (createdTemplateSource: TemplateSourceDTO) => {
                  this.templateSource = createdTemplateSource;
                  this.navService.navigateToPoint(RoutingID.templateSources, null, {
                    queryParams: { templateGroupID: this.templateGroupId },
                  });
                },
                error: error => {
                  this.errorHandler.errorHandler(error);
                },
              });
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  selectGroup() {
    this.modal
      .showNavigationModal({
        entryComponent: { value: TemplateGroupsComponent, data: { selectedMedium: this.templateGroup } },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then(result => {
        if (result) {
          this.templateGroup = result as TemplateGroupDTO;
          this.templateGroupId = this.templateGroup.id;
        }
      })
      .catch(() => {});
  }

  getFilterSelectClass() {
    if (this.templateGroup) {
      return 'btn-hover-primary--active';
    }
    return 'btn-hover-primary';
  }

  getFilterText() {
    if (this.templateGroup) {
      return this.templateGroup.name;
    }
    return this.translate.instant('lang.globals.templateGroup');
  }
}
